import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { CheckIcon, PlugIcon, RefreshCwIcon } from 'lucide-react'
import { AccountingSyncController } from '../../controllers'
import { AppState } from '../../store'
import { AccountingSyncStatus, Settings } from '../../types'
import ERoute from '../../ERoute'
import RouteHelper from '../../helpers/RouteHelper'
import moment from '../../utilities/Moment'
import Notification from '../../utilities/Notification'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import Panel from '../Panel/Panel'
import { Skeleton } from '../ui/skeleton'
import { Spinner } from '../ui/spinner'
import { useAccountingSync } from '../../hooks/use-accounting-sync'

export default function AccountingSyncCard() {
  const { t } = useTranslation()
  const history = useHistory()
  const settings = useSelector<AppState, Settings>(state => state.authentication.currentUser.workspace.setting as Settings)
  const { status, syncing, sync } = useAccountingSync<AccountingSyncStatus>({
    getStatus: () => AccountingSyncController.getStatus(),
    synchronize: () => AccountingSyncController.synchronize().then(response => response.success),
    onCompleted: () => Notification.notifySuccess(t('Accountant::Synchronization completed')),
  })

  const onSyncClick = async () => {
    if (await sync()) Notification.notifySuccess(t('Accountant::Synchronization started'))
  }

  const onConnectClick = (type: string) => {
    history.push(RouteHelper.process(ERoute.PATH_INTEGRATION, { type: type }))
  }

  if (!status) return <Skeleton className='h-72 w-full' />

  const activeIntegrations = status.integrations.filter(integration => integration.active)
  const hasActiveIntegration = activeIntegrations.length > 0
  const shownIntegrations = hasActiveIntegration ? activeIntegrations : status.integrations
  const timeFormat = settings.hour_format === '12' ? 'h:mm A' : 'HH:mm'

  return (
    <Panel title={hasActiveIntegration ? t('Accountant::Accounting integration') : t('Accountant::Connect your accounting software')}>
      <div className='flex flex-col gap-4'>
        <p className='text-sm text-muted-foreground'>
          {hasActiveIntegration
            ? t('Accountant::Your invoices, expenses and documents are automatically synchronized with your accounting software every day.')
            : t('Accountant::Integrations are the best way to share your documents with your accountant. Your invoices, expenses and documents are synchronized automatically every day.')}
        </p>

        {shownIntegrations.map(integration => (
          <div key={integration.type} className='flex items-center gap-3'>
            <img src={integration.icon} alt='' className='size-8 shrink-0 rounded object-contain' />

            <div className='min-w-0 flex-1'>
              <div className='text-sm font-medium'>{integration.name}</div>
              <div className='line-clamp-2 text-xs text-muted-foreground'>{integration.description}</div>
            </div>

            {integration.active
              ? <Badge><CheckIcon />{t('Accountant::Active')}</Badge>
              : (
                <Button variant='outline' size='sm' onClick={() => onConnectClick(integration.type)}>
                  <PlugIcon />
                  {t('Accountant::Connect')}
                </Button>
              )}
          </div>
        ))}

        {hasActiveIntegration && (
          <div className='flex flex-wrap items-center justify-between gap-2 border-t pt-4'>
            <p className='text-xs text-muted-foreground'>
              {status.last_accounting_sync_at
                ? t('Accountant::Last synchronized on {{date}}', { date: moment(status.last_accounting_sync_at).format(`${settings.date_format} ${timeFormat}`), interpolation: { escapeValue: false } })
                : t('Accountant::Not synchronized yet')}
            </p>

            <Button variant='outline' onClick={onSyncClick} disabled={syncing}>
              {syncing ? <Spinner /> : <RefreshCwIcon />}
              {syncing ? t('Accountant::Synchronizing...') : t('Accountant::Synchronize now')}
            </Button>
          </div>
        )}
      </div>
    </Panel>
  )
}
